import { LOCALES } from "app/language/model"
import { IServerPagination } from "app/model"
import { CURRENCIES } from "currency/model"
import { IDspDeal } from "dsp-deal/model"
import { CampaignAPI, ForecastAPI } from "generated-api"
import { IBoundaryBox } from "geometry/model"
import { IReportsCampaignResult } from "reports/model"
import { IPeriodFilter, ITimeFilter } from "ui/model"

export const MAP_MAX_SCREEN_IDS = 100

export const CAMPAIGN_MERGE_ERROR_CONFLICT_KEY: string = "campaign.merge.error.conflict"
export const CAMPAIGN_MERGE_ERROR_SERVER_KEY: string = "campaign.merge.error.server"
export const CAMPAIGN_MERGE_ERROR_UNKNOWN_KEY: string = "campaign.merge.error.unexpected"

export const SAVE_TIMEOUT_IN_MS = 1000
export const FILTER_HEADER_HEIGHT_IN_REM = 2.5
export const FILTER_BODY_HEIGHT_IN_REM = 9
export const FILTER_EXPANDED_HEIGHT_IN_REM = FILTER_HEADER_HEIGHT_IN_REM + FILTER_BODY_HEIGHT_IN_REM

export type ICampaignDTO = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignList>>[0]
export type ICampaignType = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignTypeList>>[0]
export type ICampaignCreateParams = Parameters<typeof CampaignAPI.postApiCampaign>[0]
export type ICampaignUpdateParams = Parameters<typeof CampaignAPI.putApiCampaign>[0]
export type IMapForecastResult = Awaited<ReturnType<typeof ForecastAPI.getApiForecastDoohMap>>
export type IMapForecastScreen = NonNullable<
  Awaited<ReturnType<typeof ForecastAPI.getApiForecastDoohMap>>["screens"]
>[0]

export enum CAMPAIGN_STATUSES {
  TEMPORARY = "temporary", // UNSAVED (create)
  PENDING = "pending", // SAVED (draft in list)
  PLANNING = "planning", // FORECAST campaign
  REVIEW = "review", // ACTIVE
  RESERVATION = "reservation", // ACTIVE
  PRICING = "pricing", // ACTIVE
  ACTIVE = "active", // LIVE
  CANCELLED = "cancelled", // CANCELLED
  EXPIRED = "expired" // FINISHED
}
export const ALL_CAMPAIGN_STATUSES: CAMPAIGN_STATUSES[] = [
  // all statuses except TEMPORARY (== buff campaign) and PLANNING (== forecast)
  CAMPAIGN_STATUSES.PENDING,
  CAMPAIGN_STATUSES.REVIEW,
  CAMPAIGN_STATUSES.ACTIVE,
  CAMPAIGN_STATUSES.CANCELLED,
  CAMPAIGN_STATUSES.EXPIRED,
  CAMPAIGN_STATUSES.PRICING,
  CAMPAIGN_STATUSES.RESERVATION
]
export const ALL_FORECAST_STATUSES: CAMPAIGN_STATUSES[] = [
  CAMPAIGN_STATUSES.PLANNING // forecast = campaign with status PLANNING
]
export const CAMPAIGN_STATUSES_RUNNING_OR_RUNNED: CAMPAIGN_STATUSES[] = [
  CAMPAIGN_STATUSES.ACTIVE,
  CAMPAIGN_STATUSES.REVIEW,
  CAMPAIGN_STATUSES.PRICING,
  CAMPAIGN_STATUSES.RESERVATION,
  CAMPAIGN_STATUSES.EXPIRED
]
export const CAMPAIGN_STATUSES_ACTIVE_OR_LIVE: CAMPAIGN_STATUSES[] = [
  CAMPAIGN_STATUSES.ACTIVE,
  CAMPAIGN_STATUSES.REVIEW,
  CAMPAIGN_STATUSES.PRICING,
  CAMPAIGN_STATUSES.RESERVATION
]
export const ACTIVE_CAMPAIGN_STATUSES: CAMPAIGN_STATUSES[] = [
  CAMPAIGN_STATUSES.PRICING,
  CAMPAIGN_STATUSES.RESERVATION,
  CAMPAIGN_STATUSES.REVIEW
]
export const NON_EDITABLE_CAMPAIGN_STATUSES: CAMPAIGN_STATUSES[] = [
  CAMPAIGN_STATUSES.EXPIRED,
  CAMPAIGN_STATUSES.CANCELLED
]

const STATUS_FILTERS: IStatusFilter[] = [
  {
    labelKey: "status.reservation",
    statuses: [CAMPAIGN_STATUSES.RESERVATION, CAMPAIGN_STATUSES.REVIEW, CAMPAIGN_STATUSES.PRICING]
  },
  { labelKey: "status.pending", statuses: [CAMPAIGN_STATUSES.PENDING] },
  { labelKey: "status.active", statuses: [CAMPAIGN_STATUSES.ACTIVE] },
  { labelKey: "status.cancelled", statuses: [CAMPAIGN_STATUSES.CANCELLED] },
  { labelKey: "status.expired", statuses: [CAMPAIGN_STATUSES.EXPIRED] }
]
export const getStatusFilters = (isDspDeal?: boolean): IStatusFilter[] => {
  if (!isDspDeal) {
    return STATUS_FILTERS.slice()
  }
  return STATUS_FILTERS.filter(s => ["status.active", "status.cancelled", "status.expired"].includes(s.labelKey))
}

export type ICampaign = Omit<
  ICampaignDTO,
  "status_id" | "start_date" | "end_date" | "created_at" | "confirmed_at" | "billed_at"
> & {
  status_id: CAMPAIGN_STATUSES
  reports: IReportsCampaignResult | null
  estimate: IForecastSimpleResult | null
  dspDeal: IDspDeal | null
  start_date: Date | null
  end_date: Date | null
  created_at: Date | null
  confirmed_at: Date | null
  billed_at: Date | null
}

export enum CAMPAIGN_LANGUAGES {
  DEUTCH = "de",
  GREEK = "el",
  ENGLISH = "en",
  SPANISH = "es",
  FRENCH = "fr",
  ITALIAN = "it",
  JAPANESE = "ja",
  PORTUGESE = "pt",
  CHINESE = "zh"
}
const ALL_CAMPAIGN_LANGUAGES = [
  CAMPAIGN_LANGUAGES.ENGLISH,
  CAMPAIGN_LANGUAGES.FRENCH,
  CAMPAIGN_LANGUAGES.SPANISH,
  CAMPAIGN_LANGUAGES.CHINESE,
  CAMPAIGN_LANGUAGES.DEUTCH,
  CAMPAIGN_LANGUAGES.JAPANESE,
  CAMPAIGN_LANGUAGES.PORTUGESE,
  CAMPAIGN_LANGUAGES.ITALIAN,
  CAMPAIGN_LANGUAGES.GREEK
]
export const getAllCampaignLanguages = (): CAMPAIGN_LANGUAGES[] => ALL_CAMPAIGN_LANGUAGES.slice()
export const DEFAULT_CAMPAIGN_LANGUAGE = CAMPAIGN_LANGUAGES.ENGLISH

export interface IStatusFilter {
  statuses: CAMPAIGN_STATUSES[]
  labelKey: string
}

export interface ICampaignFilters {
  creationDate: ITimeFilter
  startEndPeriod: IPeriodFilter
  advertiserIds: number[]
  name: string
  orderIds: number[]
  statuses: CAMPAIGN_STATUSES[]
  isForecast: boolean
  ids?: number[]
}

export interface IForecastResultGroup {
  impressions: number
  screen_count: number
  worth_cad: number
}

export interface IForecastSimpleResult {
  total_screens: number
  total_impressions: number
  total_spend: number
}

export interface IForecastResult extends IForecastSimpleResult {
  cpmForecastTotalSpend: number
  cpmForecastTotalScreens: number
  cpmForecastTotalImpressions: number
  cpmForecastGroups: IForecastResultGroup[]
}

export interface IForecastResultByCampaignId {
  [campaignId: number]: IForecastSimpleResult
}

export interface IDspDealByCampaignId {
  [campaignId: number]: IDspDeal
}

export interface IMapForecastScreenData {
  ssp_id: number
  allow_image: boolean
  allow_video: boolean
  publisher_id: string
  // formats ex: "[192001080]", "[56000160,140000400]", "[76801366,136000768,136600768]",
  //             "[108001920,102400768,128000720,136000768,136600768,168001050,192001080,24000320,288001620,384002160,60000500]"
  formats: string
  screen_id: string
  screen_name: string
  venue_type_ids: string // venue_type = environment. ex: "160|162"
  temperature: string // ex: "29.7"
  weather_ids: string // ex: "3|7"
}

export interface ICampaignRepository {
  getCampaign(campaignId: number): Promise<ICampaign>
  getCampaignStatuses(): Promise<CAMPAIGN_STATUSES[]>
  getCampaignTypes(): Promise<ICampaignType[]>
  create(params: ICampaignCreateParams): Promise<ICampaign>
  getCampaigns(pagination: IServerPagination, filters: ICampaignFilters, type?: "RTB" | "Deal"): Promise<ICampaign[]>
  getCampaignsCount(filters?: ICampaignFilters, type?: "RTB" | "Deal"): Promise<number>
  update(params: ICampaignUpdateParams): Promise<ICampaign>
  getCampaignGlobalForecast(campaignId: number, cad_exchange_rate: number): Promise<IForecastResult>
  getCampaignsGlobalForecast(campaignIds: number[]): Promise<IForecastResultByCampaignId>
  getCampaignMapForecastScreens(
    campaignId: number,
    boundaryBox: IBoundaryBox,
    resolutionInMeters: number,
    screenStats?: boolean
  ): Promise<IMapForecastResult>
  duplicate(campaignId: number): Promise<ICampaign>
  merge(campaignId1: number, campaignId2: number, campaign2UpdatedAt?: string): Promise<ICampaign>
  // will set campaign status to review and send notifications
  launchCampaign(campaignId: number): Promise<ICampaign>
}

export interface ICampaignService {
  getCampaignStatuses(): Promise<CAMPAIGN_STATUSES[]>
  getCampaignTypes(): Promise<ICampaignType[]>
}

export interface ICampaignList {
  isForecast: boolean
  isDspDeal: boolean
  filters: ICampaignFilters
  isFilterExpanded: boolean
  filteredEntities: ICampaign[]
  isRefreshing: boolean
  hasRefreshError: boolean
  pagination: IServerPagination
  rowCount: number
  isLoadingReports: boolean // only used for campaigns
  isLoadingEstimates: boolean
  isLoadingDspDeal: boolean // only used for dsp deal
}

export interface ICampaignDuplicatePopup {
  campaign: ICampaign | null // null = popup is closed, else popup is shown
  isForecastConvert: boolean
  isSaving: boolean
}

export interface ICampaignState {
  campaignList: ICampaignList
  forecastList: ICampaignList
  dspDealList: ICampaignList
  statuses: string[]
  types: ICampaignType[]
  availableLanguages: CAMPAIGN_LANGUAGES[]
  duplicatePopup: ICampaignDuplicatePopup
}

export interface IDuplicateFormData {
  order_id: number | null
  advertiser_id: number | null
  language_id: CAMPAIGN_LANGUAGES
  startDate: Date | null
  endDate: Date | null
  statusId?: CAMPAIGN_STATUSES
}

export interface ICampaignRefreshListParams {
  isForecast: boolean
  isDspDeal: boolean
  pagination?: IServerPagination
  filters?: ICampaignFilters
}

export interface ICampaignServiceState {
  campaignNameById: { [campaignId: number]: string }
}

export interface ICampaignExportRow {
  currency_id: CURRENCIES
  id: number
  name: string
  status: string
  language: LOCALES
  created_at: string
  start_date: string
  end_date: string
  orderId: number
  orderName: string
  advertiserId: number
  advertiserName: string
  budget: string | null
  // reports data
  bids: number | null
  buys: number | null
  delivery_fees: number | null
  screen_count: number | null
  ecpm: string | null
  impressions: number | null
  media_count: number | null
  spend: string | null
  ssp_spend: string | null
  wins: number | null
  // estimates data
  estimate_ecpm: string | null
  estimate_total_screens: number | null
  estimate_total_impressions: number | null
  estimate_total_spend: string | null
}

export interface IDspDealExportRow {
  currency_id: CURRENCIES
  id: number
  dealid: string
  name: string
  status: string
  language: LOCALES
  created_at: string
  start_date: string
  end_date: string
  orderId: number
  orderName: string
  dspId: number
  dspName: string
  bidFloor: number
  auctionType: string
}

export interface IForecastExportRow {
  currency_id: CURRENCIES
  id: number
  name: string
  language: LOCALES
  created_at: string
  start_date: string
  end_date: string
  budget: string | null
  estimate_ecpm: string | null
  estimate_total_screens: number | null
  estimate_total_impressions: number | null
  estimate_total_spend: string | null
}
